import type RouterService from '@ember/routing/router-service';
import Service, { service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import type IntlService from 'ember-intl/services/intl';
import config from 'frontend/config/environment';
import type { Owner } from '@ember/test-helpers/build-owner';
import disableInFastboot from '@triptyk/ember-utils/decorators/disable-in-fastboot';
import { later } from '@ember/runloop';
import buildBreadcrumb from 'frontend/utils/build-breadcrumb';
// @ts-ignore
import type CookiesService from 'ember-cookies';

export default class HeadDataService extends Service {
  @service declare router: RouterService;
  @service declare intl: IntlService;
  @service declare cookies: CookiesService;

  @tracked title?: string;
  @tracked description?: string;
  @tracked noindex: boolean = false;
  @tracked shouldStructuredData: boolean = false;
  @tracked isStructuredDataCourse: boolean = false;
  @tracked keywords?: string;
  @tracked
  image = `${config.wpUrl}/wp-content/uploads/2024/04/placeholder-action.png`;
  @tracked type = 'website';
  @tracked url = this.currentUrl;
  @tracked hasCookieForAnalytics: boolean = false;

  constructor(owner: Owner) {
    super(owner);
    this.description = this.intl.t('routes.application.description');
    this.keywords = this.intl.t('routes.application.keywords');
    this.hasCookieForAnalytics =
      this.cookies.exists('moove_gdpr_popup') &&
      JSON.parse(this.cookies.read('moove_gdpr_popup')).thirdparty === '1';
  }

  get applicationName() {
    return config.applicationName;
  }

  get currentUrl() {
    return `${config.frontUrl}${this.router.currentURL}`;
  }

  get structuredData() {
    const url = this.currentUrl;
    let course = this.isStructuredDataCourse
      ? [
          {
            '@type': 'Course',
            name: this.title,
            description: this.description ?? `Formation ${this.title}`,
            provider: { '@type': 'Organization', name: 'TechnofuturTIC' },
            image: [this.image],
          },
        ]
      : [];
    return JSON.stringify({
      '@context': 'https://schema.org',
      '@graph': [
        ...course,
        {
          '@type': 'BreadcrumbList',
          '@id': `${url}#breadcrumb`,
          itemListElement: buildBreadcrumb(url, this.title),
        },
      ],
    });
  }

  @disableInFastboot
  setUrl() {
    later(
      this,
      () => {
        // Cannot use this.router because it's not init in before or afterModel, idk why
        // eslint-disable-next-line no-restricted-globals
        const currentRoute = window.location.href;
        this.url = currentRoute;
      },
      100
    );
  }
}

// DO NOT DELETE: this is how TypeScript knows how to look up your services.
declare module '@ember/service' {
  // eslint-disable-next-line no-unused-vars
  interface Registry {
    'head-data': HeadDataService;
  }
}
